import Swal from 'sweetalert2';
import api from './api';

/**
 * Approve or reject a self-assigned task from the chat - the card in the
 * thread, a card in the Tasks panel, or the task drawer they open.
 *
 * The same PUT /tasks/:id/approval the Self-Assigned tab uses, so the rules
 * (who may decide, a reason on every rejection) live on the server once. That
 * route also brings the chat's approval cards up to date over the socket.
 *
 * @param {string} taskId
 * @param {'Approved'|'Rejected'} decision
 * @param {string} [who] first name of the employee, for the reject prompt
 * @returns {Promise<{decision: string, note: string}|null>} null when the
 *   person backed out or the server refused (the reason has been shown).
 */
export const decideTask = async (taskId, decision, who = 'them') => {
    let note = '';

    // A rejection without a reason leaves the employee nothing to act on, so
    // the server requires one and so does this.
    if (decision === 'Rejected') {
        const { value, isConfirmed } = await Swal.fire({
            title: 'Reject this task?',
            input: 'textarea',
            inputLabel: `Tell ${who} what to change`,
            inputPlaceholder: 'e.g. Log this under the Spectra project, not office work',
            inputValidator: (v) => (!v || !v.trim()) && 'Please give a reason',
            showCancelButton: true,
            confirmButtonColor: '#dc2626',
            confirmButtonText: 'Reject'
        });
        if (!isConfirmed) return null;
        note = value.trim();
    }

    try {
        await api.put(`/tasks/${taskId}/approval`, { decision, note });
        Swal.fire({
            toast: true,
            position: 'top-end',
            icon: 'success',
            title: decision === 'Approved' ? 'Approved' : 'Rejected',
            showConfirmButton: false,
            timer: 1800
        });
        return { decision, note };
    } catch (err) {
        Swal.fire('Error', err.response?.data?.message || 'Could not record the decision.', 'error');
        return null;
    }
};
